import { useState, useEffect, useRef } from 'react';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Filter,
  Download,
  Fingerprint,
  Users,
  Settings,
  MapPin,
  Link as LinkIcon,
  CheckCircle2,
  AlertCircle,
  Trash2,
  BarChart3,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Wallet,
  IndianRupee,
  Timer,
  Award,
} from 'lucide-react';
import { AttendanceRecord, AttendanceStats, Staff, staffService, attendanceService, settingsService } from '@/lib/storage';
import { FaceRegistrationModal } from '@/components/FaceRegistrationModal';
import { getLocalDateString } from '@/lib/utils';
import FaceManagementModal from '@/components/FaceManagementModal';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const AdminAttendancePage = () => {
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const { toast } = useToast();
  const [dateFilter, setDateFilter] = useState(getLocalDateString());
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [staffFilter, setStaffFilter] = useState('all');
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [faceManagementModalOpen, setFaceManagementModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [entryTime, setEntryTime] = useState('09:00');
  const [exitTime, setExitTime] = useState('18:00');
  const [officeLatitude, setOfficeLatitude] = useState('');
  const [officeLongitude, setOfficeLongitude] = useState('');
  const [officeRadius, setOfficeRadius] = useState(100);
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [locationSaved, setLocationSaved] = useState(false);
  const [linkParseError, setLinkParseError] = useState('');
  const [detectingLoc, setDetectingLoc] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [allAttendance, allStaff, officeHoursSetting, locationSetting] = await Promise.all([
        attendanceService.getAll(),
        staffService.getAll(),
        settingsService.get('officeHours'),
        settingsService.get('officeLocation')
      ]);
      setAttendance(allAttendance);
      setStaffList(allStaff);
      if (officeHoursSetting?.value) {
        let val = officeHoursSetting.value;
        if (typeof val === 'string') {
          try { val = JSON.parse(val); } catch (e) { console.error(e); }
        }
        if (val && typeof val === 'object') {
          setEntryTime(val.entryTime || '09:00');
          setExitTime(val.exitTime || '18:00');
        }
      }
      if (locationSetting?.value) {
        let val = locationSetting.value;
        if (typeof val === 'string') {
          try { val = JSON.parse(val); } catch (e) { console.error(e); }
        }
        if (val && typeof val === 'object') {
          setOfficeLatitude(val.latitude?.toString() || '');
          setOfficeLongitude(val.longitude?.toString() || '');
          setOfficeRadius(val.radius || 100);
        }
      }
    } catch (error) {
      console.error("Failed to load attendance data", error);
    }
  };

  const saveOfficeHours = async (entry: string, exit: string) => {
    try {
      await settingsService.update('officeHours', { entryTime: entry, exitTime: exit });
      setEntryTime(entry);
      setExitTime(exit);
      await loadData();
    } catch (error) {
      console.error("Failed to save office hours", error);
    }
  };

  const handleDeleteAttendance = async (id: string) => {
    if (confirm("Are you sure you want to delete this attendance record? This will also revert any wages credited to the employee's wallet and delete the corresponding expense entry.")) {
      try {
        await attendanceService.delete(id);
        toast({
          title: "Success",
          description: "Attendance record deleted successfully."
        });
        await loadData();
      } catch (error) {
        console.error("Failed to delete attendance record", error);
        toast({
          title: "Error",
          description: "Failed to delete attendance record.",
          variant: "destructive"
        });
      }
    }
  };

  const parseGoogleMapsLink = (link: string) => {
    setLinkParseError('');
    if (!link.trim()) {
      setLinkParseError('Please paste a Google Maps link');
      return;
    }

    try {
      let lat: number | null = null;
      let lng: number | null = null;

      // 1. Check for standard coordinate pair anywhere in the link (e.g. search/22.7537,90.4125 or place/22.7537,90.4125)
      const coordMatch = link.match(/([-+]?\d{1,3}\.\d+)\s*,\s*([-+]?\d{1,3}\.\d+)/);
      if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lng = parseFloat(coordMatch[2]);
      }

      // 2. Fallbacks for other structures
      if (lat === null || lng === null) {
        const qMatch = link.match(/[?&]q=([-\d.]+),([-\d.]+)/);
        if (qMatch) {
          lat = parseFloat(qMatch[1]);
          lng = parseFloat(qMatch[2]);
        }
      }

      if (lat === null || lng === null) {
        const atMatch = link.match(/@([-\d.]+),([-\d.]+)/);
        if (atMatch) {
          lat = parseFloat(atMatch[1]);
          lng = parseFloat(atMatch[2]);
        }
      }

      if (lat === null || lng === null) {
        const placeMatch = link.match(/@([-\d.]+),([-\d.]+)/);
        if (placeMatch) {
          lat = parseFloat(placeMatch[1]);
          lng = parseFloat(placeMatch[2]);
        }
      }

      if (lat === null || lng === null) {
        const directMatch = link.match(/^([-\d.]+)\s*,\s*([-\d.]+)$/);
        if (directMatch) {
          lat = parseFloat(directMatch[1]);
          lng = parseFloat(directMatch[2]);
        }
      }

      if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          setLinkParseError('Invalid coordinates. Latitude: -90 to 90, Longitude: -180 to 180.');
          return;
        }
        setOfficeLatitude(lat.toString());
        setOfficeLongitude(lng.toString());
        setLocationSaved(false);
      } else {
        setLinkParseError('Could not parse coordinates. Ensure it matches coordinate patterns.');
      }
    } catch {
      setLinkParseError('Invalid format.');
    }
  };

  const saveOfficeLocation = async () => {
    try {
      const lat = parseFloat(officeLatitude);
      const lng = parseFloat(officeLongitude);
      const radius = parseInt(officeRadius.toString());

      if (isNaN(lat) || lat < -90 || lat > 90) {
        setLinkParseError('Latitude must be -90 to 90');
        return;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setLinkParseError('Longitude must be -180 to 180');
        return;
      }
      if (isNaN(radius) || radius < 10 || radius > 1000) {
        setLinkParseError('Radius must be 10m to 1000m');
        return;
      }

      await settingsService.update('officeLocation', {
        latitude: lat,
        longitude: lng,
        radius: radius
      });
      setLocationSaved(true);
      setLinkParseError('');
      await loadData();
      setTimeout(() => setLocationSaved(false), 3000);
    } catch (error) {
      console.error("Failed to save office location", error);
      setLinkParseError('Failed to save location.');
    }
  };

  const handleUseCurrentLocation = () => {
    setDetectingLoc(true);
    setLinkParseError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOfficeLatitude(pos.coords.latitude.toString());
        setOfficeLongitude(pos.coords.longitude.toString());
        setLocationSaved(false);
        setDetectingLoc(false);
        toast({
          title: 'Location Detected',
          description: `Coordinates set to ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`,
        });
      },
      (err) => {
        setLinkParseError(err.message || 'Could not retrieve current location.');
        setDetectingLoc(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const filteredAttendance = attendance.filter(record => {
    const matchesDate = !dateFilter || record.date === dateFilter;
    const matchesStaff = staffFilter === 'all' || record.staffId === staffFilter;
    return matchesDate && matchesStaff;
  });

  const getStaffName = (staffId: string) => {
    const staff = staffList.find(s => s.id === staffId);
    return staff?.name || 'Unknown';
  };

  const formatTime = (isoString?: string) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatHM = (minutes: number) => {
    const h = Math.floor(Math.abs(minutes) / 60);
    const m = Math.round(Math.abs(minutes) % 60);
    return `${String(h).padStart(2, '0')}H ${String(m).padStart(2, '0')}M`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'present':
        return <Badge variant="success" className="rounded-lg text-[10px] font-bold">On Time</Badge>;
      case 'late':
        return <Badge variant="warning" className="rounded-lg text-[10px] font-bold">Late</Badge>;
      case 'absent':
        return <Badge variant="destructive" className="rounded-lg text-[10px] font-bold">Absent</Badge>;
      default:
        return <Badge variant="secondary" className="rounded-lg text-[10px] font-bold">{status}</Badge>;
    }
  };

  const todayStats = {
    total: staffList.length,
    present: attendance.filter(a => a.date === dateFilter && a.status === 'present').length,
    late: attendance.filter(a => a.date === dateFilter && a.status === 'late').length,
    absent: staffList.length - attendance.filter(a => a.date === dateFilter).length,
  };

  const handleVerifyAttendance = async (id: string, action: 'approved' | 'rejected') => {
    try {
      await attendanceService.update(id, { verification: action });
      toast({
        title: action === 'approved' ? 'Attendance Approved' : 'Attendance Rejected',
        description: `Successfully ${action} the outside-zone attendance record.`,
      });
      loadData();
    } catch (error) {
      console.error("Failed to verify attendance", error);
      toast({
        title: 'Operation failed',
        description: 'Could not update verification status.',
        variant: 'destructive'
      });
    }
  };

  const handleResetAttendance = async () => {
    if (confirm('⚠️ WARNING: Are you sure you want to delete ALL attendance records? This action is permanent and cannot be undone.')) {
      try {
        await attendanceService.resetAll();
        toast({
          title: 'Database Reset Successful',
          description: 'All attendance records have been deleted.',
        });
        loadData();
      } catch (error) {
        console.error("Failed to reset attendance", error);
        toast({
          title: 'Reset failed',
          description: 'Could not reset attendance database.',
          variant: 'destructive'
        });
      }
    }
  };

  const exportToCSV = () => {
    const headers = ['Staff Name', 'Date', 'Clock In', 'Clock Out', 'Working Hours', 'Status'];
    const rows = filteredAttendance.map(record => [
      getStaffName(record.staffId),
      record.date,
      formatTime(record.clockIn),
      formatTime(record.clockOut),
      record.workingHours?.toFixed(2) || '-',
      record.status,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${dateFilter}.csv`;
    a.click();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Header Panel */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Attendance Logs</h2>
          <p className="text-sm text-slate-500">View team check-in status, configure geofencing and registers.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
          <Button
            variant="royal"
            onClick={() => setRegistrationModalOpen(true)}
            className="flex-1 sm:flex-none justify-center gap-2 rounded-xl font-semibold shadow-md shadow-primary/5"
          >
            <Fingerprint className="w-4.5 h-4.5" /> Register Face
          </Button>
          <Button
            variant="outline"
            onClick={() => setFaceManagementModalOpen(true)}
            className="flex-1 sm:flex-none justify-center gap-2 rounded-xl font-semibold border-slate-200 dark:border-slate-800"
          >
            <Users className="w-4.5 h-4.5" /> Manage Faces
          </Button>
          <Button
            variant="outline"
            onClick={() => setSettingsModalOpen(true)}
            className="flex-1 sm:flex-none justify-center gap-2 rounded-xl font-semibold border-slate-200 dark:border-slate-800"
          >
            <Settings className="w-4.5 h-4.5" /> Configure Geofence
          </Button>
          <Button
            variant="outline"
            onClick={exportToCSV}
            className="flex-1 sm:flex-none justify-center gap-2 rounded-xl font-semibold border-slate-200 dark:border-slate-800"
          >
            <Download className="w-4.5 h-4.5" /> Export Logs
          </Button>
        </div>
      </div>

      {/* Statistics Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Team', value: todayStats.total, color: 'text-slate-900 dark:text-white', bg: 'bg-slate-100/50 dark:bg-slate-900/50' },
          { label: 'On Time Today', value: todayStats.present, color: 'text-emerald-650 dark:text-emerald-400', bg: 'bg-emerald-500/5 dark:bg-emerald-500/10' },
          { label: 'Late Clock Ins', value: todayStats.late, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/5 dark:bg-amber-500/10' },
          { label: 'Absent today', value: todayStats.absent, color: 'text-rose-600 dark:text-rose-450', bg: 'bg-rose-500/5 dark:bg-rose-500/10' },
        ].map(s => (
          <motion.div key={s.label} variants={itemVariants}>
            <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
              <CardContent className={`p-4.5 text-center ${s.bg} rounded-2xl`}>
                <p className={`text-2xl font-black ${s.color} leading-none`}>{s.value}</p>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-2">{s.label}</p>
              </CardContent>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      {/* Pending Location Verification Requests */}
      {attendance.filter(a => a.verification === 'pending').length > 0 && (
        <motion.div variants={itemVariants} className="space-y-3 bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              Pending Location Verification Notifications
            </h3>
            <Badge variant="warning" className="text-[10px] font-bold">
              {attendance.filter(a => a.verification === 'pending').length} Actions Required
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {attendance.filter(a => a.verification === 'pending').map((record) => (
              <GlassCard key={record.id} className="border-amber-200/40 dark:border-amber-900/20 bg-white/80 dark:bg-slate-900/60 shadow-sm rounded-2xl">
                <CardContent className="p-5 flex flex-col justify-between h-full gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">
                        {record.staffName || getStaffName(record.staffId)}
                      </h4>
                      <p className="text-xs text-slate-500 font-medium flex items-center gap-1 mt-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        {new Date(record.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <Badge variant="warning" className="text-[9px] font-bold rounded-md uppercase shrink-0">
                      Outside Zone
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs border-t border-slate-100 dark:border-slate-800/40 pt-3">
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Clock Times</p>
                      <p className="font-bold text-slate-700 dark:text-slate-350 mt-1">
                        In: {formatTime(record.checkIn || record.clockIn)}
                      </p>
                      {record.checkOut && (
                        <p className="font-bold text-slate-700 dark:text-slate-350">
                          Out: {formatTime(record.checkOut)}
                        </p>
                      )}
                    </div>
                    {record.latitude && record.longitude && (
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">GPS Location</p>
                        <a
                          href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-1 font-bold text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <MapPin className="w-3.5 h-3.5" />
                          View on Maps
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 border-t border-slate-100 dark:border-slate-800/40 pt-3.5">
                    <Button
                      size="sm"
                      onClick={() => handleVerifyAttendance(record.id, 'approved')}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold h-9"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleVerifyAttendance(record.id, 'rejected')}
                      className="flex-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-xl text-xs font-bold h-9"
                    >
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </GlassCard>
            ))}
          </div>
        </motion.div>
      )}

      {/* Filters Toolbar */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3">
        <div 
          onClick={() => dateInputRef.current?.showPicker()}
          className="flex items-center gap-2.5 rounded-xl border border-slate-200 dark:border-slate-800 px-3 bg-white/50 dark:bg-slate-950/40 w-full sm:max-w-[240px] h-11 cursor-pointer"
        >
          <Calendar className="w-4.5 h-4.5 text-slate-400 shrink-0" />
          <Input
            ref={dateInputRef}
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border-none bg-transparent focus-visible:ring-0 p-0 text-sm font-medium cursor-pointer"
          />
        </div>

        <Select value={staffFilter} onValueChange={setStaffFilter}>
          <SelectTrigger className="h-11 w-full sm:w-[220px] rounded-xl border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-950/40">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <SelectValue placeholder="Filter by Member" />
            </div>
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all">All Members</SelectItem>
            {staffList.map((staff) => (
              <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </motion.div>

      {/* Attendance Logs List Table */}
      <motion.div variants={itemVariants}>
        <GlassCard className="border-slate-200/50 dark:border-slate-850 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-100/55 dark:bg-slate-900/40 border-b border-slate-100 dark:border-slate-800">
                <TableRow className="border-none">
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-6">Staff Member</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-4">Date</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-4">Clock In</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-4">Clock Out</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-4">Hours Worked</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-4">Status</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-4">Location Verification</TableHead>
                  <TableHead className="font-bold text-slate-600 dark:text-slate-450 h-12 px-6 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAttendance.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400 font-medium bg-white/30 dark:bg-slate-900/10">
                      No attendance logs recorded for the selected query parameters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAttendance.map((record) => (
                    <TableRow key={record.id} className="hover:bg-slate-100/10 dark:hover:bg-slate-800/10 border-b border-slate-100 dark:border-slate-850 transition-colors">
                      <TableCell className="font-bold text-slate-800 dark:text-slate-200 px-6 py-4">
                        {record.staffName || getStaffName(record.staffId)}
                      </TableCell>
                      <TableCell className="text-slate-500 font-medium px-4 py-4">{record.date}</TableCell>
                      <TableCell className="px-4 py-4">
                        <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 font-medium">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {formatTime(record.checkIn || record.clockIn)}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-300 font-medium px-4 py-4">
                        {record.checkOut || record.clockOut ? (
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            {formatTime(record.checkOut || record.clockOut)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-bold text-slate-800 dark:text-slate-200 px-4 py-4">
                        {record.workingHours ? formatHM(record.workingHours * 60) : '—'}
                      </TableCell>
                      <TableCell className="px-4 py-4">{getStatusBadge(record.status)}</TableCell>
                      <TableCell className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {record.verification === 'pending' && (
                            <Badge variant="warning" className="rounded-lg text-[10px] font-bold">Pending Approval</Badge>
                          )}
                          {record.verification === 'approved' && (
                            <Badge variant="success" className="rounded-lg text-[10px] font-bold">Approved</Badge>
                          )}
                          {record.verification === 'rejected' && (
                            <Badge variant="destructive" className="rounded-lg text-[10px] font-bold">Rejected</Badge>
                          )}
                          {(record.verification === 'verified' || !record.verification) && (
                            <Badge variant="secondary" className="rounded-lg text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-350">Verified</Badge>
                          )}
                          {record.latitude && record.longitude && (
                            <a
                              href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-blue-500 transition-colors"
                              title="View GPS coordinates on Google Maps"
                            >
                              <MapPin className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteAttendance(record.id)}
                          className="h-8 w-8 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-lg"
                          title="Delete attendance record"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </GlassCard>
      </motion.div>

      {/* Face Registration Modal */}
      <FaceRegistrationModal
        open={registrationModalOpen}
        onOpenChange={setRegistrationModalOpen}
        staffList={staffList}
        onSuccess={loadData}
      />

      {/* Face Management Modal */}
      <FaceManagementModal
        open={faceManagementModalOpen}
        onOpenChange={setFaceManagementModalOpen}
        onFaceDeleted={loadData}
      />

      {/* Settings Dialog (Geofence & Late settings) */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Attendance & Geofence Settings</DialogTitle>
            <DialogDescription className="text-sm">Configure shift starting hours and office boundary limits.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Shift start threshold */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Official Entry Time</label>
                <Input
                  type="time"
                  value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value || '09:00')}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Official Exit Time</label>
                <Input
                  type="time"
                  value={exitTime}
                  onChange={(e) => setExitTime(e.target.value || '18:00')}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                />
              </div>
            </div>

            {/* Office Location Geofence */}
            <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-450 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-rose-500" />
                Allowed Office Coordinates
              </h4>

              {/* Paste Google Maps Link */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <LinkIcon className="w-3.5 h-3.5" />
                  Extract Location from Google Maps Link
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Paste link or coordinates (e.g. 23.8103, 90.4125)"
                    value={googleMapsLink}
                    onChange={(e) => setGoogleMapsLink(e.target.value)}
                    className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => parseGoogleMapsLink(googleMapsLink)}
                    className="rounded-xl border-slate-200 dark:border-slate-800"
                  >
                    Parse
                  </Button>
                </div>
              </div>

              {/* Detect Location Button */}
              <Button
                type="button"
                variant="outline"
                disabled={detectingLoc}
                onClick={handleUseCurrentLocation}
                className="w-full h-10 rounded-xl border-dashed border-indigo-300 dark:border-indigo-800 text-indigo-600 dark:text-indigo-450 hover:text-indigo-550 font-semibold gap-2"
              >
                {detectingLoc ? (
                  <>
                    <span className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                    Detecting current location...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4 text-indigo-500" /> Set coordinates to my current GPS location
                  </>
                )}
              </Button>

              {/* Coordinates Inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Latitude</label>
                  <Input
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    placeholder="Latitude coordinate"
                    value={officeLatitude}
                    onChange={(e) => { setOfficeLatitude(e.target.value); setLocationSaved(false); }}
                    className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Longitude</label>
                  <Input
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    placeholder="Longitude coordinate"
                    value={officeLongitude}
                    onChange={(e) => { setOfficeLongitude(e.target.value); setLocationSaved(false); }}
                    className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                  />
                </div>
              </div>

              {/* Radius meter */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Radius boundary range (meters)</label>
                <Input
                  type="number"
                  min="10"
                  max="1000"
                  value={officeRadius}
                  onChange={(e) => { setOfficeRadius(parseInt(e.target.value) || 100); setLocationSaved(false); }}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                />
              </div>

              {/* Notification logs */}
              {linkParseError && (
                <p className="text-xs text-rose-500 font-semibold flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {linkParseError}
                </p>
              )}
              {locationSaved && (
                <p className="text-xs text-emerald-650 dark:text-emerald-400 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 animate-pulse" />
                  Geofence parameters saved successfully!
                </p>
              )}

              <Button
                variant="outline"
                size="default"
                onClick={saveOfficeLocation}
                className="w-full rounded-xl border-slate-200 dark:border-slate-800 font-semibold"
              >
                <MapPin className="w-4 h-4 mr-1.5 text-rose-500" />
                Apply Coordinates
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100 dark:border-slate-800/50">
            <Button variant="outline" onClick={() => {
              setSettingsModalOpen(false);
              setLinkParseError('');
              setLocationSaved(false);
            }} className="rounded-xl">
              Close
            </Button>
            <Button onClick={async () => {
              await saveOfficeHours(entryTime, exitTime);
              await saveOfficeLocation();
              setSettingsModalOpen(false);
              setLinkParseError('');
              setLocationSaved(false);
            }} className="rounded-xl" variant="royal">
              Apply All Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default AdminAttendancePage;
